import { AgendaProvider } from '../context/AgendaContext';
import { TimerProvider } from '../context/TimerContext';
import { ChallengeProvider } from '../context/ChallengeContext';
import { AppShell } from '../components/Layout/AppShell';
import { Header } from '../components/Layout/Header';
import { Container } from '../components/Layout/Container';
import { LiveAgendaTracker } from '../components/LiveAgendaTracker/LiveAgendaTracker';
import { EventInfo } from '../components/EventInfo/EventInfo';
import { AgendaList } from '../components/Agenda/AgendaList';
import { ForgeChallenge } from '../components/Challenges/ForgeChallenge';
import { SideQuests } from '../components/Challenges/SideQuests';
import { SplashScreen, useSplashScreen } from '../components/SplashScreen';
import { useVisibleSections } from '../hooks/useVisibleSections';
import styles from '../App.module.css';

/**
 * EventPage Component - Main event page
 * 
 * Features:
 * - Wraps application with context providers (Agenda, Timer, Challenge)
 * - Renders all sections (LiveAgendaTracker, EventInfo, AgendaList, ForgeChallenge, SideQuests)
 * - Implements section navigation with URL hash support
 * - Applies AppShell layout component
 * - Responsive Grid layout (1 col mobile, 2 col desktop with 1.1fr 0.9fr ratio)
 * - Proper spacing between sections (--s-4, --s-5)
 * - Conditional rendering based on time-based visibility
 */
export function EventPage() {
  const { showSplash, dismissSplash } = useSplashScreen();
  const { isSectionVisible } = useVisibleSections();

  if (showSplash) {
    return <SplashScreen onDismiss={dismissSplash} />;
  }

  return (
    <AgendaProvider>
      <TimerProvider>
        <ChallengeProvider>
          <AppShell>
            <div className={styles.app}>
              <Header />
              
              <main className={styles.main}>
                <Container>
                  {/* Live Agenda Tracker - Full Width - Always visible */}
                  <section id="live" className={`${styles.section} ${styles.fullWidth}`}>
                    <LiveAgendaTracker />
                  </section>

                  {/* Event Info - Full Width - Conditionally visible */}
                  {isSectionVisible('info') && (
                    <section id="info" className={`${styles.section} ${styles.fullWidth}`}>
                      <EventInfo />
                    </section>
                  )}

                  {/* Two Column Layout for Desktop */}
                  <div className={styles.gridLayout}>
                    {/* Left Column - Agenda - Always visible */}
                    <div className={styles.leftColumn}>
                      <section id="agenda" className={styles.section}>
                        <AgendaList />
                      </section>
                    </div>

                    {/* Right Column - Challenges */}
                    <div className={styles.rightColumn}>
                      {/* Forge Challenge - Conditionally visible */}
                      {isSectionVisible('forge') && (
                        <section id="forge" className={styles.section}>
                          <ForgeChallenge />
                        </section>
                      )}

                      {/* Side Quests - Conditionally visible */}
                      {isSectionVisible('quests') && (
                        <section id="quests" className={styles.section}>
                          <SideQuests />
                        </section>
                      )}
                    </div>
                  </div>
                </Container>
              </main>
            </div>
          </AppShell>
        </ChallengeProvider>
      </TimerProvider>
    </AgendaProvider>
  );
}
